// src/thirdParty/consent/ConsentAutoOpen.tsx
import { useEffect, useRef } from 'react';
import { useConsent } from './ConsentProvider';

type Props = {
    enabled?: boolean;
    open?: 'preferences' | 'welcome';
    devForce?: boolean; // in DEV ggf. erzwingen (aber trotzdem nur 1x/Seite)
    resetBeforeOpen?: boolean; // nur für DEV sinnvoll
    debug?: boolean;
};

const PAGE_LOCK_KEY = '__cmp_auto_open_lock'; // verhindert Mehrfachläufe pro Pageview (StrictMode/Hot-Reload)
const USER_DECIDED_KEY = '__cmp_user_decided'; // persistiert „User hat explizit entschieden“

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function waitFor(check: () => boolean, timeout = 8000, step = 50): Promise<boolean> {
    return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
            if (check()) return resolve(true);
            if (Date.now() - start >= timeout) return resolve(false);
            setTimeout(tick, step);
        };
        tick();
    });
}

// --- UC Helfer ---
function ucIsInitialized(): boolean {
    try {
        return !!(window as any).UC_UI?.isInitialized?.();
    } catch {
        return false;
    }
}
function hasExplicitUC(): boolean {
    try {
        const list = (window as any).UC_UI?.getServicesBaseInfo?.() ?? [];
        return list.some((s: any) =>
            s?.consent?.history?.some(
                (h: any) => h?.type === 'explicit' || ['acceptAll', 'denyAll', 'save'].includes(h?.action),
            ),
        );
    } catch {
        return false;
    }
}
function openUC(layer: 'preferences' | 'welcome') {
    if (layer === 'welcome') (window as any).UC_UI?.showFirstLayer?.();
    else (window as any).UC_UI?.showSecondLayer?.() ?? (window as any).UC_UI?.showFirstLayer?.();
}

// --- consentmanager.de Helfer ---
function cmIsReady(): boolean {
    return typeof (window as any).__cmp === 'function';
}
function hasDecisionCM(): boolean {
    try {
        const data = (window as any).__cmp?.('getCMPData');
        const vc = data?.vendorConsents ?? {};
        const pc = data?.purposeConsents ?? {};
        return Object.keys(vc).length > 0 || Object.keys(pc).length > 0;
    } catch {
        return false;
    }
}
function openCM(layer: 'preferences' | 'welcome') {
    if (layer === 'welcome') (window as any).__cmp?.('showScreen');
    else (window as any).__cmp?.('showScreenAdvanced');
}

// --- Persistenz ---
function markUserDecided() {
    try {
        localStorage.setItem(USER_DECIDED_KEY, '1');
    } catch {}
}
function userAlreadyDecidedLS(): boolean {
    try {
        return localStorage.getItem(USER_DECIDED_KEY) === '1';
    } catch {
        return false;
    }
}

export default function ConsentAutoOpen({
    enabled = true,
    open = 'preferences',
    devForce = false,
    resetBeforeOpen = false,
    debug = false,
}: Props) {
    const { ready, managerName } = useConsent();
    const ranRef = useRef(false); // komponentenlokaler Guard gegen Doppelaufrufe

    // Haupt-Effect: Auto-Open (einmalig)
    useEffect(() => {
        if (!enabled || !ready) return;

        // Globaler Lock pro Pageview
        if ((window as any)[PAGE_LOCK_KEY]) return;
        if (ranRef.current) return;
        ranRef.current = true;

        const log = (...a: any[]) => {
            if (debug) console.debug('[ConsentAutoOpen]', ...a);
        };

        const isUC = managerName === 'usercentrics';
        const isCM = managerName === 'consentmanagerde';

        // Bereits entschieden? (Persist-Flag)
        if (!devForce && userAlreadyDecidedLS()) {
            log('persisted: user already decided → skip');
            return;
        }

        (async () => {
            (window as any)[PAGE_LOCK_KEY] = true;

            if (isUC) {
                // suppress-Flag sicher deaktivieren
                if ((window as any).UC_UI_SUPPRESS_CMP_DISPLAY) (window as any).UC_UI_SUPPRESS_CMP_DISPLAY = false;

                // 1) Auf echte UC-Initialisierung warten
                const ok = await waitFor(ucIsInitialized, 8000, 50);
                log('UC isInitialized:', ok);
                if (!ok) {
                    (window as any)[PAGE_LOCK_KEY] = false;
                    ranRef.current = false;
                    return;
                }

                // Optional: im DEV vor Öffnen zurücksetzen
                if (resetBeforeOpen) (window as any).UC_UI?.reset?.();

                // 2) Session wiederherstellen (wichtig für Wiederbesuch), kurz Luft lassen
                (window as any).UC_UI?.restoreUserSession?.();
                await wait(150);

                // 3) Wenn Nutzer bereits EXPLIZIT entschieden hat → nichts öffnen
                if (!devForce && hasExplicitUC()) {
                    log('UC explicit decision exists → skip auto-open');
                    markUserDecided();
                    return;
                }

                // 4) Sanft öffnen (genau EINE Aktion, kein Restart)
                await wait(250);
                openUC(open);
                log('UC open called:', open);

                return;
            }

            if (isCM) {
                // 1) Auf __cmp warten
                const ok = await waitFor(cmIsReady, 8000, 50);
                log('CM ready:', ok);
                if (!ok) {
                    (window as any)[PAGE_LOCK_KEY] = false;
                    ranRef.current = false;
                    return;
                }

                // 2) Bereits Entscheidung vorhanden?
                if (!devForce && hasDecisionCM()) {
                    log('CM decision exists → skip auto-open');
                    markUserDecided();
                    return;
                }

                // 3) Öffnen
                await wait(250);
                openCM(open);
                log('CM open called:', open);

                return;
            }

            // Kein CMP aktiv → Lock freigeben
            (window as any)[PAGE_LOCK_KEY] = false;
            ranRef.current = false;
        })();
    }, [enabled, ready, managerName, open, devForce, resetBeforeOpen, debug]);

    // Zweit-Effect: Nach expliziter Entscheidung Persist-Flag setzen
    useEffect(() => {
        const isUC = managerName === 'usercentrics';
        const isCM = managerName === 'consentmanagerde';

        // Usercentrics: offizielles Event
        if (isUC) {
            const handler = () => {
                if (hasExplicitUC()) markUserDecided();
            };
            window.addEventListener('ucConsentChanged' as any, handler as any);
            return () => window.removeEventListener('ucConsentChanged' as any, handler as any);
        }

        // consentmanager.de: Simple Poll-Heuristik (kurz nach Mount)
        if (isCM) {
            let cancelled = false;
            (async () => {
                // bis zu 10s nach expliziter Entscheidung Ausschau halten
                const start = Date.now();
                while (!cancelled && Date.now() - start < 10000) {
                    if (hasDecisionCM()) {
                        markUserDecided();
                        break;
                    }
                    await wait(500);
                }
            })();
            return () => {
                cancelled = true;
            };
        }
    }, [managerName]);

    return null;
}
